import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const activeCategoryId = slug || 'all';

  const activeCategory = categories.find((c) => c.id === activeCategoryId) || categories[0];

  const filteredArticles = activeCategoryId === 'all'
    ? articles
    : articles.filter((a) => a.category === activeCategoryId);

  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const handleCategoryChange = (categoryId: string) => {
    navigate(`/category/${categoryId}`);
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="w-full bg-background-100 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '分类浏览' },
              ]}
            />
            <div className="py-8 md:py-12">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">
                分类浏览
              </h1>
              <p className="text-foreground-600 text-sm">
                按类别浏览建站资源，找到最适合你的建站方案
              </p>
            </div>
          </div>
        </div>

        <div className="w-full px-4 md:px-6 max-w-6xl mx-auto py-10">
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="hidden lg:block w-56 flex-shrink-0">
              <div className="sticky top-24">
                <h3 className="text-sm font-semibold text-foreground-950 mb-4 font-heading">分类筛选</h3>
                <div className="space-y-1">
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => handleCategoryChange(cat.id)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-all duration-200 text-left cursor-pointer ${
                        activeCategoryId === cat.id
                          ? 'bg-primary-100 text-primary-700 font-medium'
                          : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-800'
                      }`}
                    >
                      <i className={`${cat.icon} text-base`}></i>
                      <span className="whitespace-nowrap">{cat.name}</span>
                      {activeCategoryId === cat.id && (
                        <i className="ri-check-line ml-auto text-primary-500"></i>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="lg:hidden mb-6">
              <button
                onClick={() => setMobileFilterOpen(!mobileFilterOpen)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm font-medium text-foreground-700 cursor-pointer"
              >
                <span className="flex items-center gap-2">
                  <i className={`${activeCategory.icon} text-base`}></i>
                  {activeCategory.name}
                </span>
                <i className={`ri-arrow-down-s-line transition-transform duration-300 ${mobileFilterOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {mobileFilterOpen && (
                <div className="mt-2 p-3 rounded-xl bg-background-100 border border-background-200/70 grid grid-cols-2 gap-2">
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => {
                        handleCategoryChange(cat.id);
                        setMobileFilterOpen(false);
                      }}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm transition-all cursor-pointer ${
                        activeCategoryId === cat.id
                          ? 'bg-primary-100 text-primary-700 font-medium'
                          : 'text-foreground-600 hover:bg-background-200/50'
                      }`}
                    >
                      <i className={`${cat.icon} text-sm`}></i>
                      <span className="whitespace-nowrap">{cat.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-foreground-950 font-heading">
                  {activeCategory.name}
                  <span className="text-sm font-normal text-foreground-500 ml-2">
                    共 {filteredArticles.length} 篇
                  </span>
                </h2>
              </div>

              {filteredArticles.length === 0 ? (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                    <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500 text-sm">该分类下暂无内容</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {filteredArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.slug}`}
                      className="group block rounded-2xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <div className="relative w-full h-[200px] overflow-hidden">
                        <img
                          src={article.coverImage}
                          alt={article.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          title={article.title}
                        />
                      </div>
                      <div className="p-5">
                        <div className="flex items-center gap-3 mb-3">
                          <span className="text-xs text-foreground-500">{article.date}</span>
                          <span className="text-xs text-foreground-400">&middot;</span>
                          <span className="text-xs text-foreground-500">{article.readTime}</span>
                        </div>
                        <h3 className="text-base font-semibold text-foreground-950 mb-2 line-clamp-2 font-heading group-hover:text-primary-600 transition-colors">
                          {article.title}
                        </h3>
                        <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed">
                          {article.excerpt}
                        </p>
                        <div className="flex flex-wrap gap-2 mt-4">
                          {article.tags.map((tag) => (
                            <span
                              key={tag}
                              className="px-2.5 py-1 rounded-md bg-background-100 text-xs text-foreground-500 whitespace-nowrap"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}